import {
  Card,
  Shop,
  Employee,
  TagsDocument,
  IconNames,
  TerminologyOptions,
  PatchCardDto,
  Integrators,
} from "@/types/v2";
import { firestoreToDateTime, getTextColor } from "@/lib/utils";
import {
  getCardSection,
  updateCardWithForm,
  generateCardDetailsUrl,
  isValidCardId,
} from "@/lib/cards";
import { useFeatures } from "@/hooks/useFeatures";
import { useCardAnimations } from "@/hooks/useCardAnimations";
import { getEmployeeName } from "@/lib/employees";
import { JobHourDisplay } from "@/components/Dashboard/components/Card/JobHourDisplay";
import { TagsList } from "@/components/Common/TagsList";
import { Icon } from "@/components/Common/Icon";
import { Button } from "@/components/Base/Button";
import { Separator } from "@/components/Base/Separator";
import { Duration, formatDuration, intervalToDuration } from "date-fns";
import { container } from "@/repositories/container";
import { useCustomerUpdatesByCardId } from "@/hooks/queries/useCustomerUpdatesByCardId";
import CustomerUpdatesTimeline from "@/components/CustomerUpdate/CustomerUpdatesTimeline";
import { NotesMain } from "@/components/Dashboard/components/Card/Notes.Main";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/Base/Table";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { ExternalLinkWrapper } from "@/components/Dashboard/components/Card/ExternalLinkWrapper";
import {
  Card as BaseCard,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Base/Card";
import { CardFormDialog } from "@/components/Dashboard/components/Card/CardFormDialog";
import { Jobs } from "@/components/Cards/Jobs";
import { useStore } from "@/stores/useStore";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  CLOSED_SHOPWARE_STATUS_VALUES,
  CLOSED_TEKMETRIC_STATUS_VALUES,
} from "@/lib/const";

export interface CardDetailsViewProps {
  cardData: Card;
  shop: Shop;
  employees?: Employee[];
  tags?: TagsDocument;
  onBack?: () => void;
}

export const CardDetailsView = ({
  cardData,
  shop,
  employees,
  tags,
  onBack,
}: CardDetailsViewProps) => {
  const { features } = useFeatures(shop);
  const { terminolize } = useTerminologyReplacer();
  const { tags: storeTags, cards } = useStore();
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // `cards` only contains currently-containerized cards (see shop.slice.ts) — a
  // source card that's been completed/closed and removed from its container
  // (but not hard-deleted) will show as unavailable here even though it still
  // exists. Accepted limitation (WEBV3-300): a link-degradation nicety, not
  // worth an async existence check.
  const duplicatedFromSourceExists =
    !!cardData.duplicatedFrom?.cardId &&
    cards.some((c) => c.id === cardData.duplicatedFrom?.cardId);

  const handleDuplicatedFromClick = (event: React.MouseEvent) => {
    event.preventDefault();
    const sourceCardId = cardData.duplicatedFrom?.cardId;
    if (!sourceCardId || !isValidCardId(sourceCardId)) return;

    const url = generateCardDetailsUrl(shop.id, sourceCardId);
    if (event.ctrlKey || event.metaKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEditCard = async (values: PatchCardDto) => {
    await updateCardWithForm(shop.id, cardData.id, values, cardData, storeTags);
  };

  const { getBackgroundColor } = useCardAnimations({
    cardData: cardData,
    isCardColorSyncEnabled: features.isCardColorSyncEnabled,
    isCardColorEnabled: features.isCardColorEnabled,
    isPromiseTimeEnabled: features.isPromiseTimeEnabled,
    isDragging: false,
    startTime:
      firestoreToDateTime(cardData?.timer?.timeStarted)?.getTime() ?? null,
    promiseTimePastDueColor: shop.shopConfig?.promiseTime?.pastDueColor,
  });

  // Get all customer updates for this card
  const {
    data: customerUpdates = [],
    isLoading: isLoadingCustomerUpdates,
    error: customerUpdatesError,
  } = useCustomerUpdatesByCardId({
    shopId: shop.id,
    cardId: cardData.id,
    enabled: true,
  });

  const isManualCardLaborEnabled =
    shop?.cardConfig?.cardContents?.manualJobHoursEnabled ?? false;

  const customFormatDuration = (duration: Duration) => {
    if (
      !duration.years &&
      !duration.months &&
      !duration.days &&
      !duration.hours
    ) {
      return formatDuration(duration, { format: ["minutes", "seconds"] });
    } else if (!duration.years && !duration.months && !duration.days) {
      if (!duration.minutes) {
        return formatDuration(duration, { format: ["hours", "seconds"] });
      }
      return formatDuration(duration, { format: ["hours", "minutes"] });
    } else if (!duration.years && !duration.months) {
      if (!duration.hours && !duration.minutes)
        return formatDuration(duration, { format: ["days", "seconds"] });
      else if (!duration.hours)
        return formatDuration(duration, { format: ["days", "minutes"] });
      return formatDuration(duration, { format: ["days", "hours"] });
    } else if (!duration.years) {
      if (!duration.days && !duration.hours && !duration.minutes)
        return formatDuration(duration, { format: ["months", "seconds"] });
      else if (!duration.days && !duration.hours)
        return formatDuration(duration, { format: ["months", "minutes"] });
      else if (!duration.days)
        return formatDuration(duration, { format: ["months", "hours"] });
      return formatDuration(duration, { format: ["months", "days"] });
    }

    return formatDuration(duration, { format: ["years", "months"] });
  };

  const getTotalTimeInShop = (history: Card["history"]) => {
    if (!history || history.length === 0) return "No history available";

    const enteredAt = firestoreToDateTime(history[0].enteredAt) as Date;
    const currentDateTime = new Date();
    const duration = intervalToDuration({
      start: enteredAt,
      end: currentDateTime,
    });
    return customFormatDuration(duration);
  };

  const calculateDurationInCurrentContainer = (
    containerInfo: Card["history"][0]
  ) => {
    const enteredAt = firestoreToDateTime(containerInfo.enteredAt) as Date;
    const exitedAt = firestoreToDateTime(
      containerInfo.exitedAt ?? new Date()
    ) as Date;
    return intervalToDuration({
      start: enteredAt,
      end: exitedAt,
    });
  };

  const cardIsClosed = () => {
    if (cardData.source === Integrators.manual) {
      return (
        cardData.status === "completed" || cardData.status === "manuallyClosed"
      );
    }

    if (cardData.source === Integrators.tekmetric) {
      return CLOSED_TEKMETRIC_STATUS_VALUES.includes(
        parseInt(cardData.statusEnum ?? "0")
      );
    }

    if (cardData.source === Integrators.shopware) {
      return CLOSED_SHOPWARE_STATUS_VALUES.includes(cardData.statusEnum ?? "");
    }
  };

  const cardClosedText = () => {
    if (cardData.status === "completed") {
      return "Completed";
    } else if (cardData.status === "manuallyClosed") {
      return "Manually Closed";
    } else {
      return cardData.status;
    }
  };

  return (
    <div className="flex h-full flex-col gap-6 print:m-0 print:h-auto print:bg-white print:p-2 print:text-sm print:leading-normal print:text-black">
      {/* CardFormDialog */}
      {cardData.source === "manual" && (
        <CardFormDialog
          title="Edit"
          buttonTitle="Edit Card"
          description="Edit card. Card fields are determined by the card configuration."
          cardData={cardData}
          onSubmit={handleEditCard}
          externalTrigger
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost-alt" size="sm" onClick={onBack}>
              <Icon name={IconNames.leftArrow} size={16} />
              Back
            </Button>
          )}
          <div className="flex items-center gap-3">
            <ExternalLinkWrapper
              cardId={cardData.source === "manual" ? undefined : cardData.id}
            >
              <h1 className="text-nowrap text-3xl font-bold">
                {getCardSection(cardData, shop, "primaryField")}
              </h1>
            </ExternalLinkWrapper>
          </div>

          <div
            className="flex h-12 w-full items-center justify-center rounded-lg px-4 print:mb-4 print:h-auto print:rounded-md print:border print:border-gray-300 print:bg-gray-100 print:p-2"
            style={{
              backgroundColor: getBackgroundColor(),
              color: getTextColor(getBackgroundColor()),
            }}
          >
            <JobHourDisplay
              totalLaborHours={cardData.totalLaborHours}
              className="text-base print:text-black"
              iconSize={20}
              isManualCardOnly={features.isManualCardOnlyEnabled}
              isManualJobHoursEnabled={isManualCardLaborEnabled}
            />
          </div>

          {cardIsClosed() && (
            <div className="flex h-12 items-center justify-center rounded-lg bg-success px-4 font-bold text-success-foreground print:mb-4 print:h-auto print:rounded-md print:border print:border-gray-300 print:bg-gray-100 print:bg-none print:p-2 print:text-black">
              <Icon name={IconNames.circleCheck} size={20} className="mr-2" />
              <span>{cardClosedText()}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {cardData.source === "manual" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditDialogOpen(true)}
            >
              <Icon name={IconNames.edit} size={16} className="mr-1" />
              Edit
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Icon name={IconNames.print} size={16} className="mr-1" />
            Print
          </Button>
        </div>
      </div>

      {/* Print-only Header */}
      <div className="hidden print:mb-6 print:block">
        <div className="print:text-center">
          <h1 className="print:mb-2 print:text-2xl print:font-bold print:text-black">
            {getCardSection(cardData, shop, "primaryField")}
          </h1>
          <p className="print:text-lg print:text-gray-600">
            {terminolize(
              "Repair Order",
              TerminologyOptions.repairOrder,
              "singular"
            )}{" "}
            Details - {shop.name}
          </p>
        </div>
      </div>

      {/* Card Header with Labor Hours */}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 print:block print:gap-2 print:space-y-2">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2 print:space-y-2">
          {/* Card Information */}
          <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
            <CardHeader>
              <CardTitle className="text-xl print:text-lg">
                {terminolize(
                  "Repair Order",
                  TerminologyOptions.repairOrder,
                  "singular"
                )}{" "}
                Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-2xl">
                {getCardSection(cardData, shop, "secondaryField")}
              </div>
              <div className="text-xl">
                {getCardSection(cardData, shop, "tertiaryField")}
              </div>

              <Separator />

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Icon name={IconNames.appointment} size={16} />
                  <span className="font-medium">Appointment:</span>
                  <span>
                    {cardData?.appointment?.startAt
                      ? firestoreToDateTime(
                          cardData?.appointment?.startAt
                        )?.toLocaleString()
                      : "No appointment set"}
                  </span>
                  {cardData?.appointment?.endAt &&
                    cardData?.appointment?.startAt &&
                    " - "}
                  <span>
                    {cardData?.appointment?.endAt
                      ? firestoreToDateTime(
                          cardData?.appointment?.endAt
                        )?.toLocaleString()
                      : "No appointment set"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Icon name={IconNames.dueOut} size={16} />
                  <span className="font-medium">Promised Time:</span>
                  <span>
                    {cardData.promisedTime
                      ? firestoreToDateTime(
                          cardData.promisedTime
                        )?.toLocaleString()
                      : "No promised time set"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Icon name={IconNames.waiter} size={16} />
                  <span className="font-medium">Waiter:</span>
                  <span>{cardData?.isWaiter ? "Yes" : "No"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Icon name={IconNames.container} size={16} />
                  <span className="font-medium">Current Container:</span>
                  <span>
                    {container.getContainerName(cardData.currentContainer)}
                  </span>
                </div>
              </div>

              {cardData?.label && (
                <div className="flex items-center gap-2">
                  <Icon name={IconNames.tag} size={16} />
                  <span className="font-medium">Label:</span>
                  <div
                    className="flex justify-center rounded-lg px-2 font-bold text-white"
                    style={{ backgroundColor: cardData?.backgroundColor }}
                  >
                    {cardData?.label}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Icon name={IconNames.tags} size={16} />
                <span className="font-medium">Tags:</span>
                <TagsList tags={cardData?.tags ?? []} allTags={tags?.tags} />
              </div>
            </CardContent>
          </BaseCard>

          <Jobs
            jobs={cardData.jobs}
            employees={employees}
            serviceWriterId={cardData.serviceWriterId}
          />

          {/* Customer Updates */}
          <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
            <CardHeader>
              <CardTitle className="text-xl print:text-lg">
                Customer Updates
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingCustomerUpdates ? (
                <div className="justify-centerpy-8 flex items-center">
                  <div className="flex items-center gap-2">
                    <Icon
                      name={IconNames.loader}
                      className="h-5 w-5 animate-spin"
                    />
                    <span>Loading customer updates...</span>
                  </div>
                </div>
              ) : customerUpdatesError ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Icon
                    name={IconNames.warning}
                    className="mb-2 h-8 w-8 text-destructive"
                  />
                  <h3 className="mb-1 text-lg font-medium text-destructive">
                    Error Loading Customer Updates
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Unable to load customer updates. Please try again.
                  </p>
                </div>
              ) : (
                <div className="overflow-auto">
                  <CustomerUpdatesTimeline
                    updates={customerUpdates}
                    card={cardData}
                    compact={false}
                    initiallyOpenCount={3}
                    showHeader={false}
                    emptyState={
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Icon
                          name="bell"
                          className="mb-2 h-8 w-8 text-muted-foreground"
                        />
                        <h3 className="mb-1 text-lg font-medium text-muted-foreground">
                          No Customer Updates
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          There are no customer updates for this{" "}
                          {terminolize(
                            "repair order",
                            TerminologyOptions.repairOrder,
                            "singular"
                          )}{" "}
                          yet.
                        </p>
                      </div>
                    }
                  />
                </div>
              )}
            </CardContent>
          </BaseCard>

          {/* Container History */}
          {cardData?.history?.length > 0 && (
            <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
              <CardHeader>
                <CardTitle className="text-xl print:text-lg">
                  Container History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="print:overflow-visible">
                  <Table className="space-y-2 print:space-y-0 print:overflow-visible">
                    <TableHeader>
                      <TableHead className="text-primary print:bg-gray-100 print:text-black">
                        Container
                      </TableHead>
                      <TableHead className="text-primary print:bg-gray-100 print:text-black">
                        Entered At
                      </TableHead>
                      <TableHead className="text-primary print:bg-gray-100 print:text-black">
                        Exited At
                      </TableHead>
                      <TableHead className="text-right text-primary print:bg-gray-100 print:text-black">
                        Duration
                      </TableHead>
                    </TableHeader>
                    <TableBody>
                      {cardData.history.map((containerInfo, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-left">
                            {container.getContainerName(
                              containerInfo.currentContainerId
                            )}
                          </TableCell>
                          <TableCell>
                            {firestoreToDateTime(
                              containerInfo.enteredAt
                            )?.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {containerInfo.exitedAt
                              ? firestoreToDateTime(
                                  containerInfo.exitedAt
                                )?.toLocaleString()
                              : "Present"}
                          </TableCell>
                          <TableCell className="text-right">
                            {customFormatDuration(
                              calculateDurationInCurrentContainer(containerInfo)
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Separator />
                <div className="grid w-full grid-cols-2 gap-2 pt-4 text-sm font-semibold">
                  <div>Total Time in Shop</div>
                  <div className="text-right">
                    {getTotalTimeInShop(cardData.history)}
                  </div>
                </div>
              </CardContent>
            </BaseCard>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6 print:space-y-2">
          {/* Customer Information */}
          {cardData.customer && Object.keys(cardData.customer).length > 0 && (
            <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Icon
                    name={IconNames.user}
                    size={20}
                    className="text-muted-foreground"
                  />
                  <CardTitle className="text-xl print:text-lg">
                    Customer Information
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {cardData.customer.firstName && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-muted-foreground">
                      Name
                    </label>
                    <p className="text-base font-medium">
                      {`${cardData.customer.firstName} ${cardData.customer.lastName || ""}`.trim()}
                    </p>
                  </div>
                )}
                {cardData.customer.phone &&
                  cardData.customer.phone.length > 0 && (
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-muted-foreground">
                        Phone
                      </label>
                      <p className="text-base">
                        {cardData.customer.phone[0].number}
                      </p>
                    </div>
                  )}
              </CardContent>
            </BaseCard>
          )}

          {/* Vehicle Information */}
          {cardData.vehicle && Object.keys(cardData.vehicle).length > 0 && (
            <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Icon
                    name={IconNames.car}
                    size={20}
                    className="text-muted-foreground"
                  />
                  <CardTitle className="text-xl print:text-lg">
                    Vehicle Information
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="flex flex-row gap-4">
                {cardData.vehicle.year && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Year
                    </label>
                    <p>{cardData.vehicle.year}</p>
                  </div>
                )}
                {cardData.vehicle.make && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Make
                    </label>
                    <p>{cardData.vehicle.make}</p>
                  </div>
                )}
                {cardData.vehicle.model && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Model
                    </label>
                    <p>{cardData.vehicle.model}</p>
                  </div>
                )}
              </CardContent>
            </BaseCard>
          )}

          {/* Technician and Service Advisor */}
          {(cardData?.technicianId !== "none" ||
            cardData?.serviceWriterId !== "none") && (
            <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Icon
                    name={IconNames.clipboardCheck}
                    size={20}
                    className="text-muted-foreground"
                  />
                  <CardTitle className="text-xl print:text-lg">
                    Assignment
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Technician
                    </label>
                    <p>
                      {getEmployeeName(cardData?.technicianId, employees ?? [])}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Service Advisor
                    </label>
                    <p>
                      {getEmployeeName(
                        cardData?.serviceWriterId,
                        employees ?? []
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </BaseCard>
          )}

          {/* Appointment Information */}
          <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Icon
                  name={IconNames.calendar}
                  size={20}
                  className="text-muted-foreground"
                />
                <CardTitle className="text-xl print:text-lg">
                  Appointment Information
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-4">
                  {cardData.dropOffTime && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Drop-off Time
                      </label>
                      <p>
                        {firestoreToDateTime(
                          cardData.dropOffTime
                        )?.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {cardData.promisedTime && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Promised Time
                      </label>
                      <p>
                        {firestoreToDateTime(
                          cardData.promisedTime
                        )?.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  {cardData.appointment?.startAt && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Start Time
                      </label>
                      <p>
                        {firestoreToDateTime(
                          cardData.appointment.startAt
                        )?.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {cardData.appointment?.endAt && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        End Time
                      </label>
                      <p>
                        {firestoreToDateTime(
                          cardData.appointment.endAt
                        )?.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </BaseCard>
          {/* Financial Information */}
          <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Icon
                  name={IconNames.dollarSign}
                  size={20}
                  className="text-muted-foreground"
                />
                <CardTitle className="text-xl print:text-lg">
                  Financial Information
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Labor Sales
                  </label>
                  <p>
                    $
                    {cardData.laborSales
                      ? (cardData.laborSales / 100).toFixed(2)
                      : "0.00"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Parts Sales
                  </label>
                  <p>
                    $
                    {cardData.partsSales
                      ? (cardData.partsSales / 100).toFixed(2)
                      : "0.00"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Total Sales
                  </label>
                  <p>
                    $
                    {(
                      ((cardData.laborSales || 0) +
                        (cardData.partsSales || 0)) /
                      100
                    ).toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </BaseCard>

          {/* Card Metadata */}
          <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Icon
                  name={IconNames.edit}
                  size={20}
                  className="text-muted-foreground"
                />
                <CardTitle className="text-xl print:text-lg">
                  Metadata
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {terminolize(
                    "Repair Order",
                    TerminologyOptions.repairOrder,
                    "singular"
                  )}{" "}
                  Number
                </label>
                <p>{cardData.cardNumber || "N/A"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Source
                </label>
                <p className="capitalize">{cardData.source}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Status
                </label>
                <p>{cardData.status}</p>
              </div>
              {cardData.createdAt && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Created
                  </label>
                  <p>
                    {firestoreToDateTime(
                      cardData.createdAt
                    )?.toLocaleDateString()}
                  </p>
                </div>
              )}
              {cardData.enteredContainerAt && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Entered Current Container
                  </label>
                  <p>
                    {firestoreToDateTime(
                      cardData.enteredContainerAt
                    )?.toLocaleString()}
                  </p>
                </div>
              )}
              {cardData.duplicatedFrom && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Duplicated From
                  </label>
                  {duplicatedFromSourceExists ? (
                    <p>
                      <a
                        href={generateCardDetailsUrl(
                          shop.id,
                          cardData.duplicatedFrom.cardId
                        )}
                        onClick={handleDuplicatedFromClick}
                        className="underline-offset-2 hover:text-primary hover:underline"
                      >
                        {cardData.duplicatedFrom.cardNumber || "View original"}
                      </a>
                    </p>
                  ) : (
                    <p className="text-muted-foreground">
                      {cardData.duplicatedFrom.cardNumber || "Unknown"} (no
                      longer available)
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </BaseCard>

          {/* Notes */}
          <BaseCard className="bg-white print:break-inside-avoid print:border-gray-300 print:p-2">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Icon
                  name={IconNames.message}
                  size={20}
                  className="text-muted-foreground"
                />
                <CardTitle className="text-xl print:text-lg">Notes</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <NotesMain
                cardData={cardData}
                shopId={shop.id}
                autoFocusNewNote={false}
              />
            </CardContent>
          </BaseCard>

          {/* Timer Information */}
          {cardData.timer && (
            <div className="rounded-lg border bg-white p-6 print:break-inside-avoid print:border-gray-300 print:p-2">
              <h2 className="mb-4 text-xl font-semibold print:mb-2 print:text-lg print:text-black">
                Timer
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Limit
                  </label>
                  <p>{cardData.timer.timeLimitInMinutes} minutes</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    State
                  </label>
                  <p className="capitalize">
                    {cardData.timer.state || "stopped"}
                  </p>
                </div>
                {cardData.timer.timeStarted && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Started
                    </label>
                    <p>
                      {firestoreToDateTime(
                        cardData.timer.timeStarted
                      )?.toLocaleString()}
                    </p>
                  </div>
                )}
                {cardData.timer.accumulatedTime && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Accumulated
                    </label>
                    <p>
                      {Math.round(cardData.timer.accumulatedTime / 60)} minutes
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
